import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useQuoteCalculation } from './quote/useQuoteCalculation';
import QuoteCalculator from './quote/QuoteCalculator';
import QuotePreview from './quote/QuotePreview';
import { QuoteConfidenceCard } from './quote/QuoteConfidenceCard';
import SaveProgressNudge from './quote/SaveProgressNudge';
import QuotePhotoAttacher from './quote/QuotePhotoAttacher';
import QuoteActionButtons from './quote/QuoteActionButtons';
import PhotoEstimator from './quote/PhotoEstimator';
import { TEMPLATES } from '../utils/pdfTemplates';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuoteGenerator = ({ embedded = false }) => {
  const { isAuthenticated, user } = useAuth();
  const [selectedTemplate, setSelectedTemplate] = useState('professional');
  const [projectPhotos, setProjectPhotos] = useState([]);
  const [branding, setBranding] = useState({});
  const [quotaInfo, setQuotaInfo] = useState(null);

  const quoteCalculation = useQuoteCalculation();

  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const response = await axios.get(`${API}/branding/branding`, { withCredentials: true });
        setBranding(response.data);
      } catch {
        // ignore — defaults are fine
      }
    };
    if (isAuthenticated) {
      fetchBranding();
      checkQuota();
    }

    // Auto-apply any AI estimate stashed from the AI Estimate page
    try {
      const stashed = sessionStorage.getItem('quotesnapp_ai_estimate');
      if (stashed) {
        applyAIEstimate(JSON.parse(stashed));
        sessionStorage.removeItem('quotesnapp_ai_estimate');
      }
    } catch {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const checkQuota = async () => {
    try {
      const response = await axios.post(`${API}/auth/check-quota`, {}, { withCredentials: true });
      setQuotaInfo(response.data);
    } catch (error) {
      console.error('Error checking quota:', error);
    }
  };

  // When AI estimator returns a result, prefill the quote builder
  // AND surface the uploaded photos so the PDF generator can embed them.
  const applyAIEstimate = (estimate, estimatePhotos) => {
    if (!estimate) return;
    if (estimate.project_type) quoteCalculation.setProjectType(estimate.project_type);
    const v = String(estimate.measurement_value ?? '');
    switch (estimate.measurement_type) {
      case 'linear_feet':
        quoteCalculation.setLinearFeet(v);
        break;
      case 'hourly':
        quoteCalculation.setHours(v);
        break;
      case 'count':
        quoteCalculation.setCount(v);
        break;
      default:
        quoteCalculation.setSquareFeet(v);
    }
    // Set a custom material rate so total roughly matches the AI suggestion
    const measurement = Number(estimate.measurement_value || 0);
    if (measurement > 0 && estimate.estimated_material_cost) {
      quoteCalculation.setCustomMaterialRate(
        (Number(estimate.estimated_material_cost) / measurement).toFixed(2)
      );
    }
    // Carry photos into the PDF generation pipeline.
    if (Array.isArray(estimatePhotos) && estimatePhotos.length) {
      setProjectPhotos(estimatePhotos);
    }
  };

  return (
    <div className={`grid lg:grid-cols-2 gap-8 ${embedded ? '' : 'max-w-7xl mx-auto'}`}>
      <div className="space-y-6">
        <PhotoEstimator
          onApply={applyAIEstimate}
          compact
          defaultProjectType={quoteCalculation.projectType}
        />
        <QuoteCalculator
          customerName={quoteCalculation.customerName}
          setCustomerName={quoteCalculation.setCustomerName}
          selectedTemplate={selectedTemplate}
          setSelectedTemplate={setSelectedTemplate}
          projectType={quoteCalculation.projectType}
          setProjectType={quoteCalculation.setProjectType}
          squareFeet={quoteCalculation.squareFeet}
          setSquareFeet={quoteCalculation.setSquareFeet}
          linearFeet={quoteCalculation.linearFeet}
          setLinearFeet={quoteCalculation.setLinearFeet}
          hours={quoteCalculation.hours}
          setHours={quoteCalculation.setHours}
          count={quoteCalculation.count}
          setCount={quoteCalculation.setCount}
          laborRate={quoteCalculation.laborRate}
          setLaborRate={quoteCalculation.setLaborRate}
          markup={quoteCalculation.markup}
          setMarkup={quoteCalculation.setMarkup}
          customMaterialRate={quoteCalculation.customMaterialRate}
          setCustomMaterialRate={quoteCalculation.setCustomMaterialRate}
          selectedAddOns={quoteCalculation.selectedAddOns}
          toggleAddOn={quoteCalculation.toggleAddOn}
          projectTypes={quoteCalculation.projectTypes}
          setProjectTypes={quoteCalculation.setProjectTypes}
          addOns={quoteCalculation.addOns}
          setAddOns={quoteCalculation.setAddOns}
          getProjectTypeData={quoteCalculation.getProjectTypeData}
          refreshLibrary={quoteCalculation.refreshLibrary}
          quote={quoteCalculation.quote}
          user={user}
          TEMPLATES={TEMPLATES}
        />

        {/* Free for everyone — attach photos that flow into the PDF gallery.
            Separate from PhotoEstimator (Pro AI) so even free-tier contractors
            can ship inspection-style PDFs. */}
        <QuotePhotoAttacher
          projectPhotos={projectPhotos}
          setProjectPhotos={setProjectPhotos}
        />
      </div>

      <div className="space-y-4">
        <QuotePreview
          quote={quoteCalculation.quote}
          customerName={quoteCalculation.customerName}
          projectType={quoteCalculation.projectType}
          squareFeet={quoteCalculation.squareFeet}
          markup={quoteCalculation.markup}
          onApplyMarkup={quoteCalculation.setMarkup}
        />

        {/* Conversion nudge — only renders for logged-out visitors who have
            built a substantial quote. Highest-converting moment in the funnel. */}
        <SaveProgressNudge
          user={user}
          quote={quoteCalculation.quote}
          projectTypes={quoteCalculation.projectTypes}
          addOns={quoteCalculation.addOns}
        />

        {/* Live Quote Confidence Score — appears once the contractor has ≥3
            closed deals for this trade. Hidden silently for new users. */}
        <QuoteConfidenceCard confidence={quoteCalculation.confidence} />

        {quoteCalculation.quote && (
          <div className="border-2 border-blue-200 shadow-xl bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-lg p-6">
            <QuoteActionButtons
              quote={quoteCalculation.quote}
              customerName={quoteCalculation.customerName}
              projectType={quoteCalculation.projectType}
              squareFeet={quoteCalculation.squareFeet}
              laborRate={quoteCalculation.laborRate}
              markup={quoteCalculation.markup}
              selectedTemplate={selectedTemplate}
              branding={branding}
              projectPhotos={projectPhotos}
              getProjectTypeData={quoteCalculation.getProjectTypeData}
              quotaInfo={quotaInfo}
              checkQuota={checkQuota}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default QuoteGenerator;
